import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faClock,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../common/RefreshButton";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil.js";
import PhoneChangeVerificationPopup from "../PhoneChangeVerificationPopup";
import { useTranslation } from "../../../../i18n/LanguageContext";

function UserForm({
  mode, // 'create' or 'update'
  formData,
  errors,
  loading,
  userNotFound,
  disabled = false,
  refreshing = false,
  onInputChange,
  onSubmit,
  onRefresh = null,
  // Group selection props (only used in create mode)
  availableGroups = [],
  groupsLoading = false,
  groupsError = null,
  onRefreshGroups = null,
  // User management props (only used in update mode)
  selectedUser = null,
  // Verification completion handler
  onVerificationComplete = null,
  // Message setter for showing success/error messages
  setMessage = null,
}) {
  const [
    showPhoneChangeVerificationPopup,
    setShowPhoneChangeVerificationPopup,
  ] = useState(false);
  const { t } = useTranslation();

  const handleInputChange = (e) => {
    // Pass the entire event object to the parent component
    onInputChange(e);
  };

  const handleVerifyPhoneChangeClick = () => {
    setShowPhoneChangeVerificationPopup(true);
  };

  const handlePhoneChangeVerificationComplete = () => {
    setShowPhoneChangeVerificationPopup(false);
    // Call the parent handler to refresh data after verification
    if (onVerificationComplete) {
      onVerificationComplete();
    }
  };

  const isCreateMode = mode === "create";
  const isUpdateMode = mode === "update";

  // Helper function to render verification status for a field
  const renderFieldStatus = (fieldName, isVerified, pendingValue) => {
    if (isCreateMode) return null;

    if (pendingValue) {
      return (
        <div className="field-status pending">
          <FontAwesomeIcon icon={faClock} />
          <span>{t("usermgmt.form.changePending")}</span>
        </div>
      );
    } else if (isVerified) {
      return (
        <div className="field-status verified">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{t("usermgmt.form.verified")}</span>
        </div>
      );
    } else {
      return (
        <div className="field-status unverified">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{t("usermgmt.form.notVerified")}</span>
        </div>
      );
    }
  };

  return (
    <div className="common-form-group">
      {isCreateMode && <h4>{t("usermgmt.form.basicInfo")}</h4>}

      {userNotFound ? (
        <div className="common-message warning indented-below-h4">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{t("usermgmt.form.noSelectedUser")}</span>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="common-form-row indented-below-h4">
            <div className="common-form-field">
              <label>{t("usermgmt.form.firstName")}</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={disabled}
                required
              />
              {errors.firstName && (
                <span className="common-error-text">{errors.firstName}</span>
              )}
            </div>
            <div className="common-form-field">
              <label>{t("usermgmt.form.lastName")}</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={disabled}
                required
              />
              {errors.lastName && (
                <span className="common-error-text">{errors.lastName}</span>
              )}
            </div>
          </div>
          <div className="common-form-row indented-below-h4">
            <div className="common-form-field">
              <label>{t("usermgmt.form.emailAddress")}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={disabled}
                required
              />
              {renderFieldStatus(
                "email",
                selectedUser?.emailVerified,
                selectedUser?.pendingEmail,
              )}
              {errors.email && (
                <span className="common-error-text">{errors.email}</span>
              )}
            </div>
            <div className="common-form-field">
              <label>{t("usermgmt.form.phoneNumber")}</label>
              <InternationalPhoneInput
                value={formData.phone}
                onChange={(e164Value) => {
                  // Create a synthetic event object for compatibility
                  const syntheticEvent = {
                    target: {
                      name: "phone",
                      value: e164Value,
                    },
                  };
                  onInputChange(syntheticEvent);
                }}
                error={errors.phone}
                disabled={disabled}
              />
              {renderFieldStatus(
                "phone",
                selectedUser?.phoneVerified,
                selectedUser?.pendingPhone,
              )}
              {/* Add Verify Phone Change button when there's a pending phone change */}
              {isUpdateMode && selectedUser?.pendingPhone && (
                <div style={{ marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={handleVerifyPhoneChangeClick}
                    className="common-button secondary"
                    style={{
                      padding: "0.4rem 0.8rem",
                      fontSize: "0.85rem",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {t("usermgmt.form.verifyPhoneChange")}
                  </button>
                </div>
              )}
            </div>
          </div>
          {isCreateMode && (
            <div className="common-form-row indented-below-h4">
              <div className="common-form-field">
                <label>{t("usermgmt.form.assignGroup")}</label>
                {groupsLoading ? (
                  <div className="common-loading">
                    {t("usermgmt.form.loadingGroups")}
                  </div>
                ) : groupsError ? (
                  <div className="common-form-error">
                    <span>
                      {t("usermgmt.form.errorLoadingGroups")} {groupsError}
                    </span>
                    {onRefreshGroups && (
                      <RefreshButton
                        onClick={onRefreshGroups}
                        variant="ghost"
                        size="sm"
                        label={t("common.retry")}
                        loadingLabel={t("common.retrying")}
                      />
                    )}
                  </div>
                ) : (
                  <select
                    name="groupName"
                    value={formData.groupName || ""}
                    onChange={handleInputChange}
                    disabled={disabled}
                    required
                  >
                    <option value="">{t("usermgmt.form.selectGroup")}</option>
                    {availableGroups.map((group) => (
                      <option
                        key={group.groupName || group.name}
                        value={group.groupName || group.name}
                      >
                        {group.groupName || group.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.groupName && (
                  <span className="common-error-text">
                    {t("usermgmt.form.groupRequired")}
                  </span>
                )}
              </div>
            </div>
          )}
          <div
            className="common-button-group indented-below-h4"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              type="submit"
              className="common-button primary"
              disabled={loading || disabled}
            >
              {loading
                ? isCreateMode
                  ? t("usermgmt.form.creating")
                  : t("usermgmt.form.updating")
                : isCreateMode
                  ? t("usermgmt.form.createUser")
                  : t("usermgmt.form.updateUser")}
            </button>
            {isUpdateMode && onRefresh && (
              <RefreshButton
                onClick={onRefresh}
                loading={refreshing}
                disabled={loading}
              />
            )}
          </div>
        </form>
      )}

      {/* Phone Change Verification Popup */}
      {showPhoneChangeVerificationPopup && (
        <PhoneChangeVerificationPopup
          user={selectedUser}
          onClose={() => setShowPhoneChangeVerificationPopup(false)}
          onVerificationComplete={handlePhoneChangeVerificationComplete}
          setMessage={setMessage}
        />
      )}
    </div>
  );
}

export default UserForm;
